import { AsyncPipe, NgClass, NgForOf, NgIf, NgStyle } from '@angular/common';
import { Component, computed, effect, Inject, OnInit, Signal, signal, ViewChild } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { MatButton } from '@angular/material/button';
import { MAT_DIALOG_DATA, MatDialog, MatDialogActions, MatDialogClose, MatDialogTitle } from '@angular/material/dialog';
import { MatIcon } from '@angular/material/icon';
import { MatProgressBar } from '@angular/material/progress-bar';
import { MatDrawer, MatDrawerContainer } from '@angular/material/sidenav';
import { MatStep, MatStepLabel, MatStepper, MatStepperIcon } from '@angular/material/stepper';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { findIndex, get } from 'lodash';
import {
  combineLatest,
  debounceTime,
  delay,
  interval,
  Observable,
  of,
  startWith,
  Subscription,
  switchMap,
  take,
} from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { EDialogWidth } from '../../../core/constants/ui.constants';
import { MatPillComponent } from '../../../shared/components/mat-pill/mat-pill.component';
import { ScwFormioComponent } from '../../../shared/components/scw-formio/scw-formio.component';
import { IFormioChangeEvent, IFormioSubmitEvent } from '../../../shared/components/scw-formio/scw-formio.interface';
import { TextAvatarComponent } from '../../../shared/components/text-avatar/text-avatar.component';
import { ImageLoaderDirective } from '../../../shared/directives/image-loader.directive';
import { ELoadStatus } from '../../../state/state.interface';
import { API_DATE_FORMAT, DateUtilitiesService } from '../../../utilities/date-utilities.service';
import { HttpUtilitiesService } from '../../../utilities/http-utilities.service';
import { ESnackbar, SnackbarService } from '../../../utilities/snackbar.service';
import {
  IStepForm,
  IWorkInstructionStep,
  IWorkInstructionSubmission,
} from '../../settings/work-instructions/work-instruction-editor/work-instruction-steps/work-instruction-steps.interface';
import { WorkInstructionCheckInComponent } from '../items-modal/work-instruction-check-in/work-instruction-check-in.component';
import {
  ECheckInDestinationType,
  IWorkInstructionCheckInDialogData,
  IWorkInstructionCheckInStep,
} from '../items-modal/work-instruction-check-in/work-instruction-check-in.interface';
import { ChangeStepActionSelectionDialogComponent } from './change-step-action-selection-dialog/change-step-action-selection-dialog.component';
import { IWorkInstructionStepperState, WorkInstructionStepperStore } from './work-instruction-stepper-store.service';
import { IWorkInstructionStepForm, IWorkInstructionStepperParams } from './work-instruction-stepper.interface';
import { WorkInstructionStepperService } from './work-instruction-stepper.service';

@Component({
  imports: [
    MatButton,
    MatDrawer,
    MatDrawerContainer,
    TranslatePipe,
    ScwFormioComponent,
    MatDialogActions,
    MatDialogClose,
    MatStep,
    MatStepLabel,
    MatStepper,
    MatStepperIcon,
    NgForOf,
    NgIf,
    MatDialogTitle,
    MatIcon,
    AsyncPipe,
    MatPillComponent,
    MatProgressBar,
    NgStyle,
    NgClass,
    ImageLoaderDirective,
    TextAvatarComponent,
  ],
  providers: [WorkInstructionStepperStore, WorkInstructionStepperService, HttpUtilitiesService],
  selector: 'app-work-instruction-stepper',
  styleUrl: './work-instruction-stepper.component.scss',
  templateUrl: './work-instruction-stepper.component.html',
})
export class WorkInstructionStepperComponent implements OnInit {
  @ViewChild('stepper') stepper: MatStepper | undefined;

  public readonly subscriptions: Subscription[] = [];
  public workInstructionStepForms: IWorkInstructionStepForm[] = [];
  public readonly selectedStepId = signal<number | undefined>(undefined);
  public readonly selectedStep = computed(() =>
    this.store.steps().find((item: IWorkInstructionStep) => item.id === this.selectedStepId()),
  );
  public readonly selectedStepIndex = computed(() =>
    this.selectedStepId() && this.store.steps().length
      ? findIndex(this.store.steps(), (step) => step.id === this.selectedStepId())
      : 0,
  );

  public readonly selectedSubmission = computed(() =>
    this.store.submissions().find((item: IWorkInstructionSubmission) => item.stepId === this.selectedStepId()),
  );
  public readonly timerData = computed(() => {
    const selectedItemAssignedWorkInstructionStep = this.selectedSubmission();

    if (!selectedItemAssignedWorkInstructionStep?.lastPlayedAt) {
      return selectedItemAssignedWorkInstructionStep?.endedAt
        ? { buttonText: 'button.start', icon: 'play_arrow' }
        : { buttonText: 'button.resume', icon: 'play_arrow' };
    }

    return { buttonText: 'button.pause', icon: 'pause' };
  });

  public readonly formatedTotalProcessCycleTime: Signal<string | null> = computed(() => {
    const value = this.store.totalProcessCycleTime() || null;

    if (!value) {
      return null;
    }

    return DateUtilitiesService.formatSecondsToTimer(value);
  });

  public readonly formatedActualTime: Signal<string | null> = computed(() => {
    const value = this.store.actualDuration() || null;

    if (!value) {
      return null;
    }

    return DateUtilitiesService.formatSecondsToTimer(value);
  });

  public isFormValid = false;
  public draft: IFormioSubmitEvent | undefined;
  public readonly isSelectedStepTheFirstInOrder = computed(() => this.selectedStepId() === this.store.steps()?.[0]?.id);
  public readonly isSelectedStepTheLastInOrder = computed(
    () => this.selectedStepId() === this.store.steps()?.[this.store.steps()?.length - 1]?.id,
  );

  private readonly selectedSubmission$ = toObservable(this.selectedSubmission);

  public readonly selectedItemAssignedWorkInstructionStepTimer$ = this.selectedSubmission$.pipe(
    switchMap((selectedStep) => this.extractStepProgress(selectedStep)),
    map((progressInSeconds) => DateUtilitiesService.formatSecondsToTimer(progressInSeconds.time)),
  );

  public readonly allStepsTotalTimer$ = this.store.submissions$.pipe(
    switchMap((allSteps) =>
      combineLatest(allSteps.map((step) => this.extractStepProgress(step))).pipe(
        debounceTime(200),
        startWith([]),
        map((progresses) => {
          const totalTimerAsSeconds = progresses.reduce((sum, progress) => sum + progress.time, 0);

          return {
            totalTimer: DateUtilitiesService.formatSecondsToTimer(totalTimerAsSeconds),
            totalTimerAsSeconds,
          };
        }),
      ),
    ),
  );

  public readonly stepIdToProgressesTimeMap$: Observable<Map<number, { formattedTime: string; time: number }>> =
    this.store.submissions$.pipe(
      switchMap((allSteps) =>
        combineLatest(allSteps.map((step) => this.extractStepProgress(step))).pipe(
          debounceTime(200),
          map((progresses) => {
            const stepTimeMap: Map<number, { formattedTime: string; time: number }> = new Map<
              number,
              { formattedTime: string; time: number }
            >();

            progresses.forEach((progress) => {
              if (progress.stepId !== null) {
                const formatted: string = DateUtilitiesService.formatSecondsToTimer(progress.time);
                stepTimeMap.set(progress.stepId, { formattedTime: formatted, time: progress.time });
              }
            });

            return stepTimeMap;
          }),
        ),
      ),
    );

  public trackByStepId(_index: number, step: IWorkInstructionStep): number {
    return step.id;
  }

  public readonly ELoadStatus = ELoadStatus;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: IWorkInstructionStepperParams,
    public readonly store: WorkInstructionStepperStore,
    private readonly matDialog: MatDialog,
    private readonly translate: TranslateService,
    private readonly snackbarService: SnackbarService,
  ) {
    effect(() => {
      // If a step is selected for the first time, start the timer
      const selectedStepId = this.selectedStepId();
      const selectedSubmission = this.selectedSubmission();

      if (selectedStepId && !selectedSubmission?.lastPlayedAt && !selectedSubmission?.progressSinceLastPause) {
        this.store.upsertWorkInstructionSubmission({
          dto: {
            lastPlayedAt: DateUtilitiesService.getNowInUTC().format(API_DATE_FORMAT),
          },
          guideAssignmentId: this.data.itemGuideAssignmentId,
          stepId: selectedStepId,
          submissionId: selectedSubmission?.id,
        });
      }
    });
  }

  public ngOnInit(): void {
    this.store.loadStepsAndSubmissions(this.data.itemGuideAssignmentId);
    this.store.loadCheckIns(this.data.itemGuideAssignmentId);
    this.subscriptions.push(
      this.store
        .select((state: IWorkInstructionStepperState) => state.dataLoading)
        .pipe(
          filter((value: ELoadStatus) => value === ELoadStatus.success),
          delay(200), // This is added so mat-horizontal-stepper can generate the steps before this.selectedStepIndex is set
          take(1),
        )
        .subscribe(() => {
          const stepsWhichAreNotCompleted = this.store.steps().filter((step) => {
            const matchingSubmission = this.store.submissions().find((submission) => submission.stepId === step.id);

            return !matchingSubmission?.endedAt;
          });
          const requestedStepId = this.store.steps().some((step) => step.id === this.data.initialStepId)
            ? this.data.initialStepId
            : undefined;
          const initialStepId =
            requestedStepId ?? get(stepsWhichAreNotCompleted, '0.id') ?? get(this.store.steps(), '0.id');

          this.selectedStepId.set(initialStepId);
        }),
      this.store.upsertStatus$.pipe(filter((status) => status === ELoadStatus.success)).subscribe(() => {
        this.store.loadStepsAndSubmissions(this.data.itemGuideAssignmentId);
      }),
    );
  }

  /**
   * Check-ins of this work instruction and of its steps; the current step is preselected when it can be checked in to
   * (started → it has a guide submission, and not completed).
   */
  public onCheckInsClick(): void {
    const steps: IWorkInstructionCheckInStep[] = this.store
      .steps()
      .filter((step: IWorkInstructionStep) => step.submission && !step.submission.endedAt)
      .map((step: IWorkInstructionStep) => ({ guideSubmissionId: step.submission!.id, name: step.name }));
    const selectedSubmissionId: number | undefined = steps.find(
      (step: IWorkInstructionCheckInStep) => step.guideSubmissionId === this.selectedSubmission()?.id,
    )?.guideSubmissionId;

    this.subscriptions.push(
      this.matDialog
        .open(WorkInstructionCheckInComponent, {
          autoFocus: false,
          data: {
            boardId: this.data.boardId,
            initialDestination: selectedSubmissionId
              ? { objectId: selectedSubmissionId, typeId: ECheckInDestinationType.workInstructionStep }
              : { objectId: this.data.itemGuideAssignmentId, typeId: ECheckInDestinationType.workInstruction },
            itemId: this.data.itemId,
            workInstructions: [{ itemGuideAssignmentId: this.data.itemGuideAssignmentId, name: this.data.name, steps }],
          } satisfies IWorkInstructionCheckInDialogData,
          maxWidth: '100%',
          restoreFocus: false,
          width: EDialogWidth.xSmall,
        })
        .afterClosed()
        .subscribe(() => this.store.loadCheckIns(this.data.itemGuideAssignmentId)),
    );
  }

  public onClickStep(): void {
    const clickedStepIndex = findIndex(this.store.steps(), (step) => step.id === Number(this.stepper?.selected?.label));

    if (clickedStepIndex === this.selectedStepIndex()) {
      return;
    }

    /**
     * @description
     * Clicking on a step cause the step to appear to change immediately on mat-horizontal-stepper
     * but the visual step change must wait the decision on ChangeStepActionSelectionDialogComponent
     * so it can be cancelled if necessary
     */
    this.stepper!.selectedIndex = this.selectedStepIndex();
    this.changeStep(clickedStepIndex);
  }

  public onChangedForm(event: IFormioSubmitEvent | IFormioChangeEvent): void {
    const selectedStepId = this.selectedStepId();

    if (!selectedStepId) {
      return;
    }

    this.setStepForm(selectedStepId, event.data);
  }

  public pauseOrResumeStepTimer(): void {
    this.store.upsertWorkInstructionSubmission({
      dto: {
        endedAt: null,
        formData: this.draft,
        lastPlayedAt: this.selectedSubmission()?.lastPlayedAt
          ? null
          : DateUtilitiesService.getNowInUTC().format(API_DATE_FORMAT),
      },
      guideAssignmentId: this.data.itemGuideAssignmentId,
      stepId: this.selectedStepId()!,
      submissionId: this.selectedSubmission()?.id,
    });
  }

  public onSubmit($event: IFormioSubmitEvent | undefined): void {
    if (!$event || !this.isFormValid) {
      this.snackbarService.open(this.translate.instant('workInstruction.messages.invalidForm'), ESnackbar.error);

      return;
    }

    this.store.upsertWorkInstructionSubmission({
      dto: {
        endedAt: DateUtilitiesService.getNowInUTC().format(API_DATE_FORMAT),
        formData: $event,
      },
      guideAssignmentId: this.data.itemGuideAssignmentId,
      stepId: this.selectedStepId()!,
      submissionId: this.selectedSubmission()?.id,
    });
  }

  public changeStep(newIndex: number): void {
    if (newIndex === this.selectedStepIndex()) {
      return;
    }

    if (this.selectedStep()?.submission?.endedAt) {
      this.selectedStepId.set(this.store.steps()[newIndex].id);

      return;
    }

    this.subscriptions.push(
      this.matDialog
        .open(ChangeStepActionSelectionDialogComponent, {
          data: {
            isSaveAndCompleteOptionDisabled: !this.isFormValid,
          },
        })
        .afterClosed()
        .subscribe((selection: undefined | '' | 'saveAndComplete' | 'justSave'): void => {
          if (!selection) {
            return;
          }

          this.store.upsertWorkInstructionSubmission({
            dto: {
              formData: this.draft,
              ...(selection === 'saveAndComplete'
                ? { endedAt: DateUtilitiesService.getNowInUTC().format(API_DATE_FORMAT) }
                : {}),
            },
            guideAssignmentId: this.data.itemGuideAssignmentId,
            stepId: this.selectedStepId()!,
            submissionId: this.selectedSubmission()?.id,
          });

          this.selectedStepId.set(this.store.steps()[newIndex].id);
        }),
    );
  }

  public saveStep(): void {
    this.store.upsertWorkInstructionSubmission({
      dto: {
        formData: this.draft,
        ...(this.selectedSubmission()?.lastPlayedAt
          ? {}
          : { lastPlayedAt: DateUtilitiesService.getNowInUTC().format(API_DATE_FORMAT) }),
      },
      guideAssignmentId: this.data.itemGuideAssignmentId,
      stepId: this.selectedStepId()!,
      submissionId: this.selectedSubmission()?.id,
    });
  }

  private setStepForm(stepId: number, form: object): void {
    const step: IWorkInstructionStepForm = {
      boardId: this.data.boardId,
      form,
      itemId: this.data.itemId,
      stepId,
      workInstructionId: this.data.workInstructionId,
    };

    const otherSteps: IWorkInstructionStepForm[] = this.workInstructionStepForms.filter(
      (item: IStepForm) => item.stepId !== stepId,
    );

    otherSteps.push(step);

    this.workInstructionStepForms = otherSteps;
  }

  private extractStepProgress(
    selectedItemAssignedWorkInstructionStep: IWorkInstructionSubmission | undefined,
  ): Observable<{ stepId: number | null; time: number }> {
    const progressAtInitialization =
      (selectedItemAssignedWorkInstructionStep?.lastPlayedAt
        ? DateUtilitiesService.getNowInUTC().diff(
            DateUtilitiesService.buildDayjsFromISO8601DateString(selectedItemAssignedWorkInstructionStep.lastPlayedAt),
          ) / 1000
        : 0) + (selectedItemAssignedWorkInstructionStep?.progressSinceLastPause ?? 0);

    const stepId: number | null = selectedItemAssignedWorkInstructionStep?.stepId ?? null;

    return selectedItemAssignedWorkInstructionStep?.lastPlayedAt
      ? interval(1000).pipe(
          map((secondsSinceInitialization) => ({
            stepId,
            time: progressAtInitialization + secondsSinceInitialization,
          })),
        )
      : of({
          stepId,
          time: progressAtInitialization,
        });
  }
}
